import { AspectRatio, AspectRatioNumbers, AspectRatioCSSValues, AspectRatioDisplayNames } from "../types/aspect-ratios";

/**
 * Constant object for aspect ratio keys (for referencing)
 */
export const ASPECT_RATIOS = {
  SQUARE: "square",
  LANDSCAPE: "landscape",
  PORTRAIT: "portrait",
  FEED: "feed"
} as const;

/**
 * Array of all aspect ratios in preferred order
 */
export const ASPECT_RATIO_ORDER: AspectRatio[] = ["square", "landscape", "feed", "portrait"];

/**
 * Default aspect ratios for new ad asset groups
 */
export const DEFAULT_ASPECT_RATIOS: AspectRatio[] = ["square", "landscape", "feed", "portrait"];

/**
 * Aspect ratio values in standard format (width:height)
 */
export const ASPECT_RATIO_NUMBERS: AspectRatioNumbers = {
  square: "1:1",
  landscape: "1.9:1",
  portrait: "9:16",
  feed: "4:5"
};

/**
 * Aspect ratio values in CSS format (width/height)
 */
export const ASPECT_RATIO_CSS_VALUES: AspectRatioCSSValues = {
  square: "1 / 1",
  landscape: "1.9 / 1",
  portrait: "9 / 16",
  feed: "4 / 5"
};

/**
 * Human-readable display names for aspect ratios
 */
export const ASPECT_RATIO_DISPLAY_NAMES: AspectRatioDisplayNames = {
  square: "Square (1:1)",
  landscape: "Landscape (1.9:1)",
  portrait: "Portrait (9:16)",
  feed: "Feed (4:5)"
};

/**
 * Returns the preferred aspect ratio from a list of available ratios.
 * Priority order: square > landscape > feed > portrait
 *
 * This function is order-agnostic - it will always return based on priority
 * regardless of the order of the input array.
 */
export function getPreferredAspectRatio(
  availableAspectRatios: AspectRatio[],
  fallback: AspectRatio = "square"
): AspectRatio {
  if (!availableAspectRatios || availableAspectRatios.length === 0) {
    return fallback;
  }

  return ASPECT_RATIO_ORDER.find((ar) => availableAspectRatios.includes(ar)) ?? availableAspectRatios[0] ?? fallback;
}
